import React, { useEffect } from 'react';
import { Toaster } from '../components/ui/toaster';
import ReviewsMarquee from '../components/ReviewsMarquee';
import TrustBar from '../components/TrustBar';
import HammerNav from '../components/hammer/HammerNav';
import HammerHero from '../components/hammer/HammerHero';
import HammerFeatures from '../components/hammer/HammerFeatures';
import HammerSpecs from '../components/hammer/HammerSpecs';
import HammerInAction from '../components/hammer/HammerInAction';
import HammerGallery from '../components/hammer/HammerGallery';
import ServiceArea from '../components/ServiceArea';
import HammerAbout from '../components/hammer/HammerAbout';
import HammerFAQ, { HAMMER_FAQ_ITEMS } from '../components/hammer/HammerFAQ';
import HammerContact from '../components/hammer/HammerContact';
import HammerFooter from '../components/hammer/HammerFooter';
import { HAMMER_REVIEWS } from '../data/hammerReviews';

const PAGE_TITLE =
  'Absolute Hammer — Pneumatic Pile Driving Hammer for Sale | $10,500';
const META_DESCRIPTION =
  'The Absolute Hammer — a fully galvanized pneumatic pile-driving hammer built in Fort Myers, FL. Off-hammer proprietary controller, 1-year electronics warranty, ships nationwide. $10,500.';
const CANONICAL = 'https://absolutehammers.com/';

const PRODUCT_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Absolute Hammer — Pneumatic Pile Driving Hammer',
  description:
    'Fully galvanized pneumatic pile-driving hammer with off-hammer proprietary controller. Uses air pressure to both lift and drop the ram for maximum striking power. Built in Fort Myers, FL.',
  brand: { '@type': 'Brand', name: 'Absolute Marine Equipment' },
  image: 'https://absolutehammers.com/assets/hammer_hero.jpg',
  category: 'Pneumatic Pile Driver',
  offers: {
    '@type': 'Offer',
    price: '10500',
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: 'https://absolutehammers.com/#hammer-quote',
    priceValidUntil: '2027-12-31',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '4',
  },
};

/**
 * Sets document-level SEO tags without needing react-helmet. Idempotent —
 * removes any tags it previously created when the component unmounts so
 * navigating back to the barge home page restores that page's own tags.
 */
function useSeo() {
  useEffect(() => {
    const managed = [];
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    const upsertMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        managed.push(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return () => {
        if (prev != null) el.setAttribute('content', prev);
      };
    };

    const restoreDesc = upsertMeta('name', 'description', META_DESCRIPTION);
    const restoreOgTitle = upsertMeta('property', 'og:title', PAGE_TITLE);
    const restoreOgDesc = upsertMeta('property', 'og:description', META_DESCRIPTION);
    const restoreOgUrl = upsertMeta('property', 'og:url', CANONICAL);
    const restoreTwTitle = upsertMeta('name', 'twitter:title', PAGE_TITLE);
    const restoreTwDesc = upsertMeta('name', 'twitter:description', META_DESCRIPTION);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
      managed.push(canonical);
    }
    canonical.setAttribute('href', CANONICAL);

    // Product JSON-LD
    const jsonld = document.createElement('script');
    jsonld.type = 'application/ld+json';
    jsonld.id = 'hammer-product-jsonld';
    jsonld.textContent = JSON.stringify(PRODUCT_JSONLD);
    document.head.appendChild(jsonld);
    managed.push(jsonld);

    // FAQPage JSON-LD — sourced from the same HAMMER_FAQ_ITEMS array the
    // visible FAQ section renders from, so the two never drift apart.
    const faqJsonld = document.createElement('script');
    faqJsonld.type = 'application/ld+json';
    faqJsonld.id = 'hammer-faq-jsonld';
    faqJsonld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: HAMMER_FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(faqJsonld);
    managed.push(faqJsonld);

    return () => {
      document.title = prevTitle;
      restoreDesc();
      restoreOgTitle();
      restoreOgDesc();
      restoreOgUrl();
      restoreTwTitle();
      restoreTwDesc();
      if (prevCanonical && canonical) canonical.setAttribute('href', prevCanonical);
      managed.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);
}

export default function HammerLanding() {
  useSeo();

  return (
    <div className="App bg-black min-h-screen" data-testid="hammer-landing-page">
      <HammerNav />
      <main>
        <HammerHero />
        <TrustBar />
        <HammerFeatures />
        <HammerSpecs />
        <HammerInAction />
        <HammerGallery />
        <ReviewsMarquee
          testId="reviews-hammer"
          title="What Absolute Hammer owners say"
          reviews={HAMMER_REVIEWS}
          direction="ltr"
        />
        <ServiceArea />
        <HammerAbout />
        <HammerFAQ />
        <HammerContact />
      </main>
      <HammerFooter />
      <Toaster />
    </div>
  );
}
